import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Clock, Compass } from 'lucide-react';
import { DomainError } from '../domain/errors';
import VerifyFirstNotice from './VerifyFirstNotice';

/**
 * The page states every screen shares (NFR-010, R25): loading, error with a
 * retry, empty, not found, and "Not available yet". One set, so an operation
 * a later stream builds degrades the same way wherever a page calls it.
 *
 * Styled as tonal cards on the page background — no borders (DESIGN.md §2).
 */

function StateCard({
  icon,
  title,
  children,
  role,
}: {
  icon: React.ReactNode;
  title: string;
  children?: React.ReactNode;
  role?: 'alert';
}) {
  return (
    <div
      role={role}
      className="flex flex-col items-center rounded-3xl bg-card px-6 py-14 text-center"
    >
      <div className="mb-5 flex size-16 items-center justify-center rounded-full bg-muted text-wood">
        {icon}
      </div>
      <h2 className="mb-2 text-xl font-semibold text-foreground">{title}</h2>
      {children}
    </div>
  );
}

export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" aria-label={label} className="space-y-4">
      <Skeleton className="h-10 w-1/2 rounded-2xl" />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-40 rounded-3xl" />
        ))}
      </div>
    </div>
  );
}

/** A failed load. Without `onRetry` it only explains: trying again wouldn't change the answer. */
export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <StateCard
      icon={<AlertTriangle className="size-8 text-destructive" />}
      title="Something went wrong"
      role="alert"
    >
      <p className="max-w-sm text-muted-foreground">{message}</p>
      {onRetry && (
        <Button className="mt-6 rounded-full" onClick={onRetry}>
          Try again
        </Button>
      )}
    </StateCard>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <StateCard icon={icon ?? <Compass className="size-8" />} title={title}>
      {description && <p className="mb-6 max-w-sm text-muted-foreground">{description}</p>}
      {action}
    </StateCard>
  );
}

/**
 * Hidden and nonexistent look the same (FR-049): the copy never says which.
 * A listing that doesn't open — hidden, withdrawn, unpublished, deleted or
 * missing — gets one page for all of them (016 FR-027).
 */
export function NotFoundState({ variant }: { variant?: 'listing' }) {
  const listing = variant === 'listing';
  return (
    <StateCard
      icon={<Compass className="size-8" />}
      title={listing ? "You don't have access" : 'Not found'}
    >
      {!listing && (
        <p className="mb-6 max-w-sm text-muted-foreground">
          There's nothing here. Check the link, or head back to your collections.
        </p>
      )}
      <Button className="rounded-full" asChild>
        <Link to="/my-collections">Go to your collections</Link>
      </Button>
    </StateCard>
  );
}

export function NotAvailableState({ what }: { what?: string }) {
  return (
    <StateCard icon={<Clock className="size-8" />} title="Not available yet">
      <p className="max-w-sm text-muted-foreground">
        {what ? `${what} isn't available yet.` : "This isn't available yet."}
      </p>
    </StateCard>
  );
}

/**
 * A failed action, shown beside the control that tried it: "Not available
 * yet" when the repository says so, the way to verify when the account must
 * be verified first, else the outcome's own message.
 */
export function ActionError({ error, className }: { error: unknown; className?: string }) {
  if (error instanceof DomainError && error.kind === 'verificationRequired') {
    return <VerifyFirstNotice message={error.message} className={className} />;
  }
  const notYet = error instanceof DomainError && error.kind === 'notYetAvailable';
  return (
    <Alert variant="destructive" role="alert" className={className}>
      {notYet && <AlertTitle>Not available yet</AlertTitle>}
      <AlertDescription>
        {error instanceof Error && error.message ? error.message : 'Something went wrong.'}
      </AlertDescription>
    </Alert>
  );
}

/**
 * The right state for a failed load, from the repository's outcome: not
 * found, not available yet (no retry: the answer won't change until the area
 * is built), a failure to retry, or any other outcome's own explanation.
 */
export function StateForError({
  error,
  onRetry,
  what,
  variant,
}: {
  error: unknown;
  onRetry: () => void;
  /** Names the thing that isn't available yet, e.g. "Messages". */
  what?: string;
  /** Which not-found page a `notFound` shows. */
  variant?: 'listing';
}) {
  if (!(error instanceof DomainError)) {
    const message = error instanceof Error ? error.message : 'Something went wrong.';
    return <ErrorState message={message} onRetry={onRetry} />;
  }
  if (error.kind === 'notFound') return <NotFoundState variant={variant} />;
  if (error.kind === 'notYetAvailable') return <NotAvailableState what={what} />;
  return <ErrorState message={error.message} onRetry={error.retryable ? onRetry : undefined} />;
}
